<template>
  <!-- [Added by E2logy]: every survey photo still saved on this device, with
  its upload status. The photos only exist here until they reach the server. -->
  <div>
    <div class="border d-flex justify-content-between align-center mb-2 p-1">
      <router-link :to="{ name: 'router_worksession_dashboard' }">
        <b-button variant="outline-secondary" size="sm">
          <b-icon icon="arrow-left"></b-icon> volver
        </b-button>
      </router-link>
      <span class="trencadis-title">{{ $t("pending_photos_title") }}</span>
      <b-button
        variant="success"
        size="sm"
        :disabled="flush.isRunning || entries.length === 0"
        @click="onClickSendAll"
      >
        <b-spinner v-if="flush.isRunning" small></b-spinner>
        {{ $t("pending_photos_send_all") }}
      </b-button>
    </div>

    <b-alert show variant="warning" v-if="isOffline">
      {{ $t("pending_photos_offline") }}
    </b-alert>

    <b-alert show variant="success" v-if="entries.length === 0">
      {{ $t("pending_photos_empty") }}
    </b-alert>
    <template v-else>
      <!-- [Modified by E2logy]: the live count replaces the progress bar. It
      goes down by itself as each photo is sent, also during "Send all". -->
      <div class="mb-2">
        <PendingPhotosCount :isLink="false" />
        <b>{{ $t("pending_photos_keep_warning") }}</b>
      </div>
      <v-data-table
        :headers="tableHeaders"
        :items="entries"
        item-key="uuid"
        :items-per-page="50"
        :footer-props="{ 'items-per-page-options': [10, 50, 100] }"
        class="elevation-1"
      >
        <template v-slot:[`item.previewUrl`]="{ item }">
          <img
            v-if="item.previewUrl"
            :src="item.previewUrl"
            class="pending_photo_thumbnail my-1"
            alt=""
          />
        </template>
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDateTime(item.createdAt) }}
        </template>
        <template v-slot:[`item.worksessionDate`]="{ item }">
          {{ item.details?.worksessionDate || "-" }}
        </template>
        <template v-slot:[`item.pos`]="{ item }">
          <template v-if="item.details?.posName">
            {{ item.details.posId }} - {{ item.details.posName }}
          </template>
          <template v-else>-</template>
        </template>
        <template v-slot:[`item.brand`]="{ item }">
          {{ item.details?.brandName || "-" }}
        </template>
        <template v-slot:[`item.survey`]="{ item }">
          {{ item.details?.surveyName || `#${item.surveyId || "-"}` }}
        </template>
        <template v-slot:[`item.question`]="{ item }">
          {{
            item.details?.questionName || `#${item.surveyQuestionId || "-"}`
          }}
          <div
            v-if="item.details?.componentName || item.details?.proOrMatName"
            class="text-sm text-muted"
          >
            {{
              [item.details.componentName, item.details.proOrMatName]
                .filter(Boolean)
                .join(" / ")
            }}
          </div>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <span :class="item.status === 'failed' ? 'text-danger' : ''">
            {{ statusLabel(item.status) }}
          </span>
          <div v-if="isOtherUserEntry(item)" class="text-sm text-muted">
            {{ $t("pending_photos_other_user") }}
          </div>
        </template>
        <template v-slot:[`item.nextAttemptAt`]="{ item }">
          {{ item.nextAttemptAt ? formatDateTime(item.nextAttemptAt) : "-" }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <b-button
            variant="outline-danger"
            size="sm"
            :disabled="flush.isRunning || item.status === 'uploading'"
            @click="onClickRetry(item.uuid)"
          >
            {{ $t("retry") }}
          </b-button>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import PhotoQueueService from "@/services/photoQueueService";
import PendingPhotosCount from "@/components/PendingPhotosCount.vue";
import { logInfo } from "@/utils";

export default {
  name: "PendingPhotos",
  components: {
    PendingPhotosCount
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("myroute", ["isOffline"]),
    entries() {
      return Object.values(PhotoQueueService.state.statuses).sort(
        (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
      );
    },
    flush() {
      return PhotoQueueService.state.flush;
    },
    tableHeaders() {
      return [
        { text: this.$t("Photo"), value: "previewUrl", sortable: false },
        { text: this.$t("pending_photos_taken_at"), value: "createdAt" },
        {
          text: this.$t("pending_photos_worksession_date"),
          value: "worksessionDate",
          sortable: false
        },
        { text: this.$t("pos"), value: "pos", sortable: false },
        { text: this.$t("pending_photos_brand"), value: "brand", sortable: false },
        { text: this.$t("survey"), value: "survey", sortable: false },
        { text: this.$t("Question"), value: "question", sortable: false },
        { text: this.$t("status"), value: "status" },
        { text: this.$t("pending_photos_attempts"), value: "attempts" },
        {
          text: this.$t("pending_photos_last_error"),
          value: "lastError",
          sortable: false
        },
        { text: this.$t("pending_photos_next_try"), value: "nextAttemptAt" },
        { text: "", value: "actions", sortable: false }
      ];
    }
  },
  methods: {
    formatDateTime(timestamp) {
      return moment(timestamp).format("YYYY-MM-DD HH:mm");
    },
    statusLabel(status) {
      if (status === "uploading") return this.$t("photo_uploading");
      if (status === "failed") return this.$t("photo_upload_failed");
      return this.$t("photo_saved_on_device");
    },
    isOtherUserEntry(item) {
      return (
        !!item.userId && !!this.user?.id && String(item.userId) !== String(this.user.id)
      );
    },
    onClickRetry(uuid) {
      PhotoQueueService.retry(uuid);
    },
    async onClickSendAll() {
      const { remaining } = await PhotoQueueService.flushAll();
      if (remaining === 0) {
        logInfo(this.$t("pending_photos_all_sent"));
      }
    }
  },
  mounted() {
    // Refresh the list straight away instead of waiting for the next tick.
    PhotoQueueService.countPending();
  }
};
</script>

<style>
.pending_photo_thumbnail {
  width: 60px;
  height: 60px;
  object-fit: cover;
}
</style>
